import { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CARD, CardTitle } from './profileCard';

// Pill-style filter dropdown
const FilterDropdown = ({ options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-jp-line bg-jp-section text-jp-ink text-[11px] font-medium hover:border-jp-accent/60 transition-colors whitespace-nowrap"
      >
        <svg className="w-3 h-3 text-jp-accent flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 4h18v2H3zm2 7h14v2H5zm4 7h6v2H9z" />
        </svg>
        <span>{value}</span>
        <svg
          className={`w-3 h-3 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-jp-field border border-jp-line rounded-lg shadow-xl min-w-[190px]">
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs transition-colors first:rounded-t-lg last:rounded-b-lg ${
                value === opt
                  ? 'text-jp-accent bg-jp-section font-semibold'
                  : 'text-jp-muted hover:bg-jp-section hover:text-jp-ink'
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Columns displayed per stat type + standard combination
const COLUMN_CONFIGS = {
  HITTING_STANDARD:      ['GP', 'AB', 'H', 'RBI', '2B', '3B', 'HR', 'K', 'BB', 'AVG', 'OBP', 'SLG'],
  HITTING_ADVANCED:      ['ISO', 'wOBA', 'wRC+', 'OPS+', 'K%', 'BB%', 'K/BB', 'BABIP'],
  PITCHING_STANDARD:     ['APP', 'GS', 'IP', 'ERA', 'W', 'L', 'K', 'BB', 'H', 'R', 'ER', 'HR', 'HBP', 'CG', 'SHO'],
  PITCHING_ADVANCED:     ['WHIP', 'FIP', 'K%', 'BB%', 'K-BB%', 'FIP+', 'xFIP', 'ERA+', 'LOB%', 'BAA'],
  FIELDING_STANDARD:     ['GP', 'TC', 'PO', 'A', 'E', 'FLD%', 'DP', 'SBA', 'RCS', 'RCS%', 'PB', 'CI'],
  'BASE RUNNING_STANDARD': ['GP', 'R', 'TB', 'SB', 'CS'],
};

const formatDecimal = (value) => {
  if (value === null || value === undefined || value === '-') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  if (num >= 1) return num.toFixed(2);
  return num.toFixed(3).substring(1);
};

// Slugging keeps three decimals even at 1.000 or higher (1.250, not 1.25)
const formatSlg = (value) => {
  if (value === null || value === undefined || value === '-') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num >= 1 ? num.toFixed(3) : num.toFixed(3).substring(1);
};

const formatStat = (value) => {
  if (!value && value !== 0) return '-';
  return value.toString();
};
const formatRound = (value) => {
  if (!value && value !== 0) return '-';
  const n = Number(value);
  return isNaN(n) ? '-' : Math.round(n).toString();
};

const formatPct = (value) => {
  if (value === null || value === undefined || value === '-') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  // If already looks like a percentage (>1), format directly
  if (num > 1) return `${num.toFixed(1)}%`;
  return `${(num * 100).toFixed(1)}%`;
};

const getCellValue = (entry, column, statType) => {
  if (!entry) return '-';
  switch (column) {
    // Shared / Hitting Standard
    case 'GP':    return formatStat(entry.gp || entry.games_played || entry.gamesPlayed || entry.appearances);
    case 'AB':    return formatStat(entry.ab || entry.at_bats || entry.atBats);
    case 'H':
      return statType === 'PITCHING'
        ? formatStat(entry.h || entry.hits_allowed || entry.hitsAllowed)
        : formatStat(entry.h || entry.hits);
    case 'RBI':   return formatStat(entry.rbi);
    case '2B':    return formatStat(entry['2b'] || entry.doubles);
    case '3B':    return formatStat(entry['3b'] || entry.triples);
    case 'HR':
      return statType === 'PITCHING'
        ? formatStat(entry.hr || entry.home_runs_allowed || entry.homeRunsAllowed)
        : formatStat(entry.hr || entry.home_runs || entry.homeRuns);
    case 'K':
      return statType === 'PITCHING'
        ? formatStat(entry.so || entry.k || entry.strikeouts_pitched || entry.strikeoutsPitched)
        : formatStat(entry.so || entry.k || entry.strikeouts);
    case 'BB':
      return statType === 'PITCHING'
        ? formatStat(entry.bb || entry.walks_allowed || entry.walksAllowed)
        : formatStat(entry.bb || entry.walks);
    case 'AVG':   return formatDecimal(entry.avg || entry.batting_average || entry.battingAverage);
    case 'OBP':   return formatDecimal(entry.ob_pct || entry.obp || entry.on_base_percentage || entry.onBasePercentage);
    case 'SLG':   return formatSlg(entry.slg || entry.slg_pct || entry.slugging_percentage || entry.sluggingPercentage);

    // Hitting Advanced
    case 'ISO':   return formatDecimal(entry.iso);
    case 'wOBA':  return formatDecimal(entry.woba || entry.wOBA);
    case 'wRC+':  return formatRound(entry.wRC_plus || entry.wrc_plus || entry['wrc+'] || entry.wrcPlus);
    case 'OPS+':  return formatRound(entry.ops_plus || entry['ops+'] || entry.opsPlus);
    case 'K%': {
      // const kPctVal = entry.k_pct || entry.kPct || entry.K_percent || entry.strikeout_percentage;
      // if (kPctVal) return formatPct(kPctVal);
      // if (statType === 'PITCHING') {
      //   const so = Number(entry.so || entry.strikeouts_pitched || 0);
      //   const bf = Number(entry.at_bats_against || 0) + Number(entry.walks_allowed || entry.bb || 0) + Number(entry.hit_batters || entry.hbp || 0) + Number(entry.sacrifice_flies_allowed || 0) + Number(entry.sacrifice_hits_allowed || 0);
      //   if (bf > 0) return formatPct(so / bf);
      // }
      // return '-';
      const v = statType === 'PITCHING' ? entry.K_percent : entry.k_percentage;
      return formatPct(v);
    }
    case 'BB%': {
      // const bbPctVal = entry.bb_pct || entry.bbPct || entry.BB_percent || entry.walk_percentage;
      // if (bbPctVal) return formatPct(bbPctVal);
      // if (statType === 'PITCHING') {
      //   const bb = Number(entry.walks_allowed || entry.bb || 0);
      //   const bf = Number(entry.at_bats_against || 0) + Number(entry.walks_allowed || entry.bb || 0) + Number(entry.hit_batters || entry.hbp || 0) + Number(entry.sacrifice_flies_allowed || 0) + Number(entry.sacrifice_hits_allowed || 0);
      //   if (bf > 0) return formatPct(bb / bf);
      // }
      // return '-';
      const v = statType === 'PITCHING' ? entry.BB_percent : entry.bb_percentage;
      return formatPct(v);
    }
    case 'K/BB':  return formatStat(entry.k_bb || entry['k/bb'] || entry.kBb);
    case 'BABIP': return formatDecimal(entry.babip || entry.BABIP);

    // Pitching Standard
    case 'APP':   return formatStat(entry.app || entry.appearances || entry.gp || entry.games_pitched || entry.gamesPitched);
    case 'GS':    return formatStat(entry.gs || entry.games_started || entry.gamesStarted);
    case 'IP':    return formatStat(entry.ip || entry.innings_pitched || entry.inningsPitched);
    case 'ERA':   { const _e = entry.era || entry.ERA; if (_e === null || _e === undefined || _e === '-' || _e === '') return '-'; const _n = Number(_e); return isNaN(_n) ? '-' : _n.toFixed(2); }
    case 'W':     return formatStat(entry.w || entry.wins);
    case 'L':     return formatStat(entry.l || entry.losses);
    case 'R':
      return statType === 'PITCHING'
        ? formatStat(entry.r || entry.runs_allowed || entry.runsAllowed)
        : formatStat(entry.r || entry.runs);
    case 'ER':    return formatStat(entry.er || entry.earned_runs || entry.earnedRuns);
    case 'HBP':   return formatStat(entry.hbp || entry.hit_by_pitch || entry.hitByPitch);
    case 'CG':    return formatStat(entry.cg || entry.complete_games || entry.completeGames);
    case 'SHO':   return formatStat(entry.sho || entry.shutouts);

    // Pitching Advanced
    case 'WHIP':  { const _w = entry.whip || entry.WHIP; if (_w === null || _w === undefined || _w === '-' || _w === '') return '-'; const _n = Number(_w); return isNaN(_n) ? '-' : _n.toFixed(2); }
    case 'FIP':   return formatDecimal(entry.fip || entry.FIP);
    case 'K-BB%': return formatPct(entry.k_minus_bb || entry['k-bb%'] || entry.kMinusBb);
    case 'FIP+':  return formatStat(entry.fip_plus || entry['fip+'] || entry.fipPlus);
    case 'xFIP':  return formatDecimal(entry.xfip || entry.xFIP);
    case 'ERA+':  return formatStat(entry.era_plus || entry['era+'] || entry.eraPlus);
    case 'LOB%':  return formatPct(entry.lob_pct || entry['lob%'] || entry.lobPct);
    case 'BAA':   return formatDecimal(entry.baa || entry.BAA);

    // Fielding Standard
    case 'TC':    return formatStat(entry.tc || entry.totalChances);
    case 'PO':    return formatStat(entry.po || entry.putouts);
    case 'A':     return formatStat(entry.a || entry.assists);
    case 'E':     return formatStat(entry.e || entry.errors);
    case 'FLD%':  return formatDecimal(entry.fpct || entry['fld%'] || entry.fld_pct || entry.fielding_percentage || entry.fieldingPercentage);
    case 'DP':    return formatStat(entry.dp || entry.doublePlays);
    case 'SBA':   return formatStat(entry.sba || entry.stolenBasesAllowed);
    case 'RCS':   return formatStat(entry.rcs || entry.runnersCaughtStealing);
    case 'RCS%':  return formatPct(entry.rcs_pct || entry['rcs%'] || entry.rcsPct);
    case 'PB':    return formatStat(entry.pb || entry.passedBalls);
    case 'CI':    return formatStat(entry.ci || entry.catcherInterference);

    // Base Running Standard
    case 'TB':    return formatStat(entry.tb || entry.total_bases || entry.totalBases);
    case 'SB':    return formatStat(entry.sb || entry.stolen_bases || entry.stolenBases);
    case 'CS':    return formatStat(entry.cs || entry.caught_stealing || entry.caughtStealing);

    case 'VS R':  return formatStat(entry.vsRHP ?? entry.vs_r ?? entry.vsR ?? entry.avg_vs_r ?? entry.avg_vs_rhp ?? entry.vs_right ?? entry.vsRight);
    case 'VS L':  return formatStat(entry.vsLHP ?? entry.vs_l ?? entry.vsL ?? entry.avg_vs_l ?? entry.avg_vs_lhp ?? entry.vs_left ?? entry.vsLeft);

    default: return '-';
  }
};

const getYear   = (e) => e?.seasonYear || e?.year || e?.season || '-';
const getTeam   = (e) => e?.team?.name || e?.teamName || (typeof e?.team === 'string' ? e.team : '-');
const getLeague = (e, fallbackLeague) => e?.league || e?.leagueName || e?.team?.league || fallbackLeague || '-';

const parseYearStart = (e) => {
  const y = getYear(e);
  if (!y || y === '-') return -Infinity;
  const m = String(y).match(/\d{4}/);
  return m ? parseInt(m[0], 10) : -Infinity;
};
const sortByYearAsc = (arr) => [...arr].sort((a, b) => parseYearStart(a) - parseYearStart(b));

// Shows the signed-in player's stats, or another player's when `player` is passed
export const StatisticsSection = ({ player } = {}) => {
  const { profile: ownProfile } = useSelector((state) => state.playerProfile);
  const profile = player || ownProfile;

  const positionUpper = profile?.primaryPosition?.toUpperCase() || '';
  const isPitcher = ['P', 'PITCHER', 'RHP', 'LHP', 'SP', 'RP'].includes(positionUpper);

  const [statType,    setStatType]    = useState(isPitcher ? 'PITCHING' : 'HITTING');
  const [overallStat, setOverallStat] = useState('OVERALL STATS');
  const [standard,    setStandard]    = useState('STANDARD');

  const normalize = (arr) =>
    Array.isArray(arr) ? arr : arr ? [arr] : [];

  const statsData = (() => {
    const conferenceData = profile?.conferenceData;
    if (overallStat === 'REGULAR SEASON CONFERENCE' && conferenceData) {
      const pick = (key) => conferenceData[key] ? [conferenceData[key]] : [];
      switch (statType) {
        case 'HITTING':      return pick('batting');
        case 'PITCHING':     return pick('pitching');
        case 'FIELDING':     return pick('fielding');
        case 'BASE RUNNING': return pick('batting');
        default:             return pick('batting');
      }
    }
    switch (statType) {
      case 'HITTING':      return sortByYearAsc(normalize(profile?.battingStats));
      case 'PITCHING':     return sortByYearAsc(normalize(profile?.pitchingStats));
      case 'FIELDING':     return sortByYearAsc(normalize(profile?.fieldingStats));
      case 'BASE RUNNING': return sortByYearAsc(normalize(profile?.battingStats));
      default:             return sortByYearAsc(normalize(profile?.battingStats));
    }
  })();

  const configKey  = `${statType}_${standard}`;
  const baseColumns = COLUMN_CONFIGS[configKey] || COLUMN_CONFIGS[`${statType}_STANDARD`] || [];
  const columns    = baseColumns;

  const standardOptions =
    statType === 'FIELDING' || statType === 'BASE RUNNING'
      ? ['STANDARD']
      : ['STANDARD', 'ADVANCED'];

  return (
    <section className={`${CARD} min-w-0`}>
      <CardTitle title="Season Statistics" />

      {/* Filter dropdowns */}
      <div className="flex flex-wrap gap-2 mb-4">
        <FilterDropdown
          options={['HITTING', 'PITCHING', 'FIELDING', 'BASE RUNNING']}
          value={statType}
          onChange={(val) => {
            setStatType(val);
            if (val === 'FIELDING' || val === 'BASE RUNNING') setStandard('STANDARD');
          }}
        />
        <FilterDropdown
          options={['OVERALL STATS', 'REGULAR SEASON', 'REGULAR SEASON CONFERENCE', 'SUMMER BALL']}
          value={overallStat}
          onChange={setOverallStat}
        />
        <FilterDropdown
          options={standardOptions}
          value={standard}
          onChange={setStandard}
        />
      </div>

      {/* Stats Table */}
      <div className="overflow-x-auto rounded-xl border border-jp-line">
        <table className="w-full text-xs sm:text-sm" style={{ minWidth: '520px' }}>
          <thead>
            <tr className="border-b-2 border-jp-ink/20 bg-jp-section">
              <th className="text-left px-3 py-2.5 text-jp-muted font-bold whitespace-nowrap">YEAR</th>
              <th className="text-left px-3 py-2.5 text-jp-muted font-bold whitespace-nowrap">TEAM</th>
              <th className="text-left px-3 py-2.5 text-jp-muted font-bold whitespace-nowrap">LEAGUE</th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="text-right px-3 py-2.5 text-jp-muted font-bold whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {statsData.length > 0 ? (
              statsData.map((entry, i) => (
                <tr
                  key={i}
                  className="border-b border-jp-line hover:bg-jp-section transition-colors"
                >
                  <td className="px-3 py-2.5 text-jp-ink font-medium whitespace-nowrap">
                    {getYear(entry)}
                  </td>
                  <td className="px-3 py-2.5 text-jp-accent-ink font-semibold whitespace-nowrap">
                    {getTeam(entry)}
                  </td>
                  <td className="px-3 py-2.5 text-jp-muted whitespace-nowrap">
                    {getLeague(entry, profile?.team?.league)}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col}
                      className="px-3 py-2.5 text-right text-jp-ink tabular-nums whitespace-nowrap"
                    >
                      {getCellValue(entry, col, statType)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 3}
                  className="px-3 py-8 text-center text-jp-muted/80"
                >
                  No statistics available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default StatisticsSection;
